import { useEffect, useMemo, useState } from 'react';
import { Autoplay } from 'swiper/modules/index.mjs';
import { Swiper, SwiperSlide } from 'swiper/swiper-react.mjs';
import 'swiper/swiper.css';
import { productPlaceholderImage } from '../utils/assets';

const INSTAGRAM_PROFILE_URL = 'https://instagram.com/vettail.in';
const INSTAGRAM_FEED_THEME = 'light';
const MIN_LOOP_SLIDES = 8;

function ArrowIcon({ direction = 'right' }) {
  const points = direction === 'left' ? '11.5 5 7 9.5 11.5 14' : '6.5 5 11 9.5 6.5 14';

  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <polyline
        points={points}
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatFeedDate(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function buildLoopReadyPosts(items) {
  if (items.length <= 1 || items.length >= MIN_LOOP_SLIDES) {
    return items;
  }

  const loopReadyPosts = [];
  let copyIndex = 0;

  while (loopReadyPosts.length < MIN_LOOP_SLIDES) {
    items.forEach((post) => {
      loopReadyPosts.push({
        ...post,
        id: `${post.id}-loop-${copyIndex}`,
      });
    });
    copyIndex += 1;
  }

  return loopReadyPosts;
}

function getSlideJump(swiperInstance) {
  const rawSlidesPerView = swiperInstance?.params?.slidesPerView;
  const slidesPerView = typeof rawSlidesPerView === 'number'
    ? rawSlidesPerView
    : Number.parseFloat(rawSlidesPerView) || 1;

  return Math.min(4, Math.max(1, Math.floor(slidesPerView)));
}

async function fetchInstagramFeed() {
  const response = await fetch('/api/instagram');
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(payload?.error || 'Unable to load Instagram posts.');
  }

  return Array.isArray(payload?.posts) ? payload.posts : [];
}

function FeedSkeleton() {
  return (
    <div className="instagram-feed-skeleton" aria-hidden="true">
      <div className="instagram-feed-skeleton-media" />
      <div className="instagram-feed-skeleton-copy">
        <span style={{ width: '72%' }} />
        <span style={{ width: '48%' }} />
      </div>
    </div>
  );
}

function FeedFallbackCard() {
  return (
    <a
      href={INSTAGRAM_PROFILE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="instagram-feed-card instagram-feed-card--fallback"
      aria-label="Open Vettail on Instagram"
    >
      <div className="instagram-feed-media-shell instagram-feed-media-shell--fallback">
        <img
          className="instagram-feed-media"
          src={productPlaceholderImage}
          alt="Dog receiving veterinary care"
          loading="lazy"
          decoding="async"
        />
      </div>

      <div className="instagram-feed-card-body">
        <p className="instagram-feed-card-caption">
          See the newest reels, product drops, and customer shots on our Instagram profile.
        </p>
        <p className="instagram-feed-card-date">open profile</p>
      </div>
    </a>
  );
}

function FeedMedia({ post }) {
  if (post.isVideo) {
    return (
      <video
        className="instagram-feed-media"
        src={post.mediaUrl}
        poster={post.thumbnailUrl || undefined}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      />
    );
  }

  return (
    <img
      className="instagram-feed-media"
      src={post.mediaUrl || post.thumbnailUrl}
      alt={post.caption || 'Instagram post from Vettail'}
      loading="lazy"
      decoding="async"
    />
  );
}

export default function InstagramFeed() {
  const [swiper, setSwiper] = useState(null);
  const [status, setStatus] = useState('loading');
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let isActive = true;

    fetchInstagramFeed()
      .then((items) => {
        if (!isActive) return;
        setPosts(items);
        setStatus(items.length > 0 ? 'ready' : 'empty');
      })
      .catch(() => {
        if (!isActive) return;
        setStatus('empty');
      });

    return () => {
      isActive = false;
    };
  }, []);

  const displayPosts = useMemo(() => {
    if (status === 'loading') {
      return Array.from({ length: 4 }, (_, index) => ({ id: `skeleton-${index}`, skeleton: true }));
    }

    return buildLoopReadyPosts(posts);
  }, [posts, status]);

  const showNavigation = status === 'ready' && displayPosts.length > 1;

  useEffect(() => {
    if (!swiper || status !== 'ready' || displayPosts.length <= 1) return;

    swiper.update();

    if (swiper.params?.loop && typeof swiper.loopDestroy === 'function' && typeof swiper.loopCreate === 'function') {
      swiper.loopDestroy();
      swiper.loopCreate();
      swiper.slideToLoop(0, 0, false);
    }

    swiper.autoplay?.start();
  }, [swiper, status, displayPosts.length]);

  const handlePrevious = () => {
    if (!swiper) return;
    const step = getSlideJump(swiper);
    swiper.slideToLoop(swiper.realIndex - step);
  };

  const handleNext = () => {
    if (!swiper) return;
    const step = getSlideJump(swiper);
    swiper.slideToLoop(swiper.realIndex + step);
  };

  return (
    <section className="instagram-feed-section" aria-labelledby="instagram-feed-title">
      <div className={`instagram-feed-shell instagram-feed-shell--${INSTAGRAM_FEED_THEME}`}>
        <div className="instagram-feed-header">
          <div className="instagram-feed-copy">
            <h2 id="instagram-feed-title" className="instagram-feed-title">instagram</h2>
            <a
              href={INSTAGRAM_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="instagram-feed-eyebrow"
            >
              @vettail.in
            </a>
          </div>

          {showNavigation ? (
            <div className="instagram-feed-actions">
              <div className="instagram-feed-nav" aria-label="Instagram feed navigation">
                <button
                  type="button"
                  className="instagram-feed-nav-btn"
                  onClick={handlePrevious}
                  aria-label="Show previous Instagram post"
                >
                  <ArrowIcon direction="left" />
                </button>
                <button
                  type="button"
                  className="instagram-feed-nav-btn"
                  onClick={handleNext}
                  aria-label="Show next Instagram post"
                >
                  <ArrowIcon />
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {status === 'empty' ? (
          <div className="instagram-feed-fallback-grid">
            <FeedFallbackCard />
          </div>
        ) : (
          <Swiper
            key={status === 'ready' ? `instagram-feed-${displayPosts.length}` : 'instagram-feed-loading'}
            className="instagram-feed-slider"
            onSwiper={setSwiper}
            modules={[Autoplay]}
            grabCursor
            observer
            observeParents
            loop={showNavigation}
            loopAdditionalSlides={displayPosts.length}
            speed={1100}
            slidesPerView={1}
            spaceBetween={18}
            watchOverflow
            autoplay={showNavigation ? {
              delay: 3000,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
              waitForTransition: false,
            } : false}
            breakpoints={{
              700: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              1100: {
                slidesPerView: 3,
                spaceBetween: 22,
              },
              1460: {
                slidesPerView: 4,
                spaceBetween: 24,
              },
            }}
          >
            {displayPosts.map((post, index) => (
              <SwiperSlide key={post.id}>
                {post.skeleton ? (
                  <FeedSkeleton />
                ) : (
                  <a
                    href={post.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="instagram-feed-card"
                    aria-label={`Open Instagram post ${index + 1}`}
                  >
                    <div className="instagram-feed-media-shell">
                      <FeedMedia post={post} />
                    </div>

                    <div className="instagram-feed-card-body">
                      <p className="instagram-feed-card-caption">
                        {post.caption || 'Fresh from Vettail on Instagram.'}
                      </p>
                      <p className="instagram-feed-card-date">{formatFeedDate(post.timestamp)}</p>
                    </div>
                  </a>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
    </section>
  );
}
